import { useParams } from "react-router-dom";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { Alert, Button, Chip, TextField } from "@mui/material";
import moment from "moment-timezone";
import "moment/locale/tr";
import AppContext from "../../AppContext";
import { checkInBadge, undoCheckInBadge } from "../../db/firebaseMethods";

const card = {
  maxWidth: 520,
  margin: "24px auto",
  padding: 24,
  borderRadius: 16,
  background: "#fff",
  boxShadow: "0 4px 18px rgba(10, 24, 39, 0.15)",
  textAlign: "left",
};

const STATUS = {
  ok: { bg: "#e7f6ec", color: "#1b7a3d", title: "Giriş onaylandı" },
  already: { bg: "#fff4e0", color: "#b26a00", title: "Bu bilet daha önce okutuldu" },
  invalid: { bg: "#fdeaea", color: "#b3261e", title: "Geçersiz bilet" },
};

const formatTime = (ms) => moment(ms).tz("Europe/Istanbul").format("DD MMM HH:mm:ss");

// Kapıdaki görevlinin bilet QR'ını okuttuğu sayfa: /giris/<gizli anahtar>. Giriş gerekmez, anahtarı backend doğrular.
export default function GirisPage() {
  const { key: gateKey } = useParams();
  const { activeSeason } = useContext(AppContext);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const busyRef = useRef(false);
  const [cameraError, setCameraError] = useState("");
  const [result, setResult] = useState(null); // { status, name, code, workshops, checkedInAt, payload }
  const [manualCode, setManualCode] = useState("");
  const [error, setError] = useState("");

  const handleCheckIn = useCallback(async (params, payload) => {
    busyRef.current = true;
    setError("");
    try {
      const res = await checkInBadge({ ...params, gateKey });
      setResult({ ...res, payload });
    } catch (e) {
      busyRef.current = false;
      console.error("checkInBadge hatası:", e);
      setError(
        e?.code === "functions/permission-denied" || e?.code === "functions/unauthenticated"
          ? "Bu bağlantı geçerli değil."
          : `Bilet doğrulanamadı. Bağlantıyı kontrol edip tekrar deneyin. (${e?.code ?? "?"}: ${e?.message ?? e})`
      );
    }
  }, [gateKey]);

  // Kamera + QR okuma döngüsü
  useEffect(() => {
    let stream;
    let raf;
    let stopped = false;
    let lastTick = 0;

    const tick = (now) => {
      if (stopped) return;
      raf = requestAnimationFrame(tick);
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (busyRef.current || !video || !canvas || video.readyState < 2) return;
      if (now - lastTick < 150) return;
      lastTick = now;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(img.data, img.width, img.height);
      if (code?.data) handleCheckIn({ payload: code.data }, code.data);
    };

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment" } })
      .then((s) => {
        if (stopped) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        stream = s;
        videoRef.current.srcObject = s;
        videoRef.current.play();
        raf = requestAnimationFrame(tick);
      })
      .catch(() => setCameraError("Kameraya erişilemedi. Tarayıcıdan kamera iznini açın veya kodu elle girin."));

    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [handleCheckIn]);

  const next = () => {
    setResult(null);
    setError("");
    busyRef.current = false;
  };

  const undo = async () => {
    try {
      await undoCheckInBadge(result.payload, gateKey);
      next();
    } catch (e) {
      setError("Geri alınamadı. Tekrar deneyin.");
    }
  };

  const submitManual = (e) => {
    e.preventDefault();
    const code = manualCode.trim();
    if (!code || !activeSeason?.id) return;
    setManualCode("");
    handleCheckIn({ seasonId: activeSeason.id, manualCode: code }, null);
  };

  const st = result && (STATUS[result.status] ?? STATUS.invalid);

  return (
    <div style={card}>
      <h2 style={{ margin: "0 0 12px", color: "#14224f" }}>Giriş Okut</h2>

      <div style={{ display: result ? "none" : "block" }}>
        <video
          ref={videoRef}
          playsInline
          muted
          style={{ width: "100%", borderRadius: 12, background: "#000", minHeight: 240 }}
        />
        <canvas ref={canvasRef} style={{ display: "none" }} />
        {cameraError && <Alert severity="warning" sx={{ mt: 1 }}>{cameraError}</Alert>}
        <div style={{ color: "#7a8696", fontSize: 13, margin: "8px 0 16px" }}>
          QR kodu kameraya gösterin, otomatik okunur.
        </div>

        <form onSubmit={submitManual} style={{ display: "flex", gap: 8 }}>
          <TextField
            size="small"
            fullWidth
            label="Kod (örn. XII-0001)"
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
          />
          <Button type="submit" variant="outlined" sx={{ textTransform: "none", fontWeight: 700 }}>
            Onayla
          </Button>
        </form>
      </div>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {result && (
        <div style={{ background: st.bg, borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: st.color }}>{st.title}</div>
          {result.status !== "invalid" && (
            <>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#14224f", marginTop: 12 }}>
                {result.name || "—"}
              </div>
              <div style={{ color: "#7a8696", marginBottom: 10 }}>{result.code} · Konferans · Genel Giriş</div>
              {result.workshops?.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  {result.workshops.map((w) => (
                    <Chip key={w} label={w} size="small" sx={{ mr: 0.5, mb: 0.5, bgcolor: "#ede9fe", color: "#7c3aed", fontWeight: 600 }} />
                  ))}
                </div>
              )}
              {result.checkedInAt && (
                <div style={{ color: st.color, fontWeight: 600 }}>
                  {result.status === "ok" ? "Giriş saati" : "Okutulma zamanı"}: {formatTime(result.checkedInAt)}
                </div>
              )}
            </>
          )}
          {result.status === "invalid" && (
            <div style={{ color: "#7a8696", marginTop: 8 }}>Bu kod sistemde kayıtlı değil.</div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <Button variant="contained" color="secondary" onClick={next} sx={{ textTransform: "none", fontWeight: 700 }}>
              Sonraki bileti okut
            </Button>
            {result.status === "ok" && result.payload && (
              <Button variant="outlined" color="inherit" onClick={undo} sx={{ textTransform: "none" }}>
                Geri al
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
